import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import {
  Input,
  Select,
  Row,
  Col,
  InputNumber,
  Button,
  Tooltip,
  Typography,
  Radio,
} from 'antd';

import { CloseOutlined } from '@ant-design/icons';
import { sortBy } from 'lodash';

import {
  Form,
  WrapperSelect,
  ThumbnailItem,
  FullWidthSpace,
} from 'common/components';
import BrickCodeInput from './BrickCodeInput';
import ProductEditBrandModal from 'pages/product-full-view/components/product-edit-brand/ProductEditBrandModal';

import * as companySelectors from 'pages/company-profile/controllers/selectors';

import { useGetMemberInfo } from './hooks';
import { PACKAGE_LEVELS, STATUS_PRODUCT } from 'static/Constants';
import { useCheckIsProductOverlay } from 'hooks/product';
import { useGetEnumsByEnumName } from 'hooks';

import { useIntl } from 'react-intl';
import messages from 'i18n/messages/product';

import { OverlayInputWrapper } from '../overlay';

import { get, isEqual } from 'lodash';

import { useGetMemberHeaderFromMemberId } from 'common/queries/member-module';

import { ERROR_FIELD_NAME_COLLECTION } from '../preview-requirements/constants';
import {
  FormItemCategory,
  FormItemSubCategory,
  FormItemClass,
} from 'common/components/product-add/components/FormItemCategoryProduct';

import './CreateProductForm.less';

const { Option } = Select;
const { Text } = Typography;

const CreateProductForm = (props) => {
  const intl = useIntl();

  const {
    form,
    productData,
    formMode,
    brick,
    updateBrickCodeData,
    handleResetBrandId,
    setBrandId,
    editBrandVisible,
    setEditBrandVisible,
    brandThumb,
    setBrandThumb,
    errorInfo,
    memberIdBrand,
    originProductFull,
    productFull,
  } = props;

  const isProductOverlay = useCheckIsProductOverlay({
    productFull: formMode === 'edit' ? productFull : null,
  });

  const userMemberInfo =
    useSelector(companySelectors.selectMemberProfile()) ?? {};

  const { memberInfo: companyInfo } = useGetMemberInfo(memberIdBrand);
  const { data: memberHeaderOfProductOwner } = useGetMemberHeaderFromMemberId({
    memberId: productData?.ownerId,
    enabled: Boolean(productData && productData?.ownerId),
  });

  const isEdit = formMode === 'edit';

  const { data: memberHeaderOfProductCreator } = useGetMemberHeaderFromMemberId(
    {
      memberId: memberIdBrand,
      enabled: !!memberIdBrand,
    },
  );

  const handleNumberInput = (event) => {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  };

  const initFieldsError = () => {
    if (!errorInfo) return;

    form.setFields(errorInfo);
  };

  const isPrivateVisibilityProduct = () => {
    if (formMode === 'create') {
      if (companyInfo || memberIdBrand) {
        return (
          memberHeaderOfProductCreator?.products?.toLowerCase() === 'private'
        );
      }
    }

    if (formMode === 'edit') {
      return memberHeaderOfProductOwner?.products?.toLowerCase() === 'private';
    }

    //* default return;
    return userMemberInfo?.products?.toLowerCase() === 'private';
  };

  useEffect(() => {
    initFieldsError();
  }, []);

  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({
        packageLevel: PACKAGE_LEVELS[0],
        isPrivateLabel: true,
      });
    }
  }, [isEdit]);

  const brandError = errorInfo?.find((errorItem) =>
    errorItem.name.includes(ERROR_FIELD_NAME_COLLECTION.BRAND),
  );

  const brandErrorMessage = brandError ? brandError.errors[0] : '';
  const getOverlayProps = (path) => {
    const originValue = get(originProductFull, path);

    return {
      checkShowOverlayFn: ({ value }) => {
        return (
          !isEqual(value ?? null, originValue ?? null) &&
          isProductOverlay &&
          !!originProductFull
        );
      },
      originValue: originValue || 'N/A',
    };
  };

  const getBrickCodeOverlayProps = () => {
    const brickCode =
      brick?.brickCode?.toString() !== originProductFull?.brickCode?.toString();

    const originValue = originProductFull?.brickCode || 'N/A';
    return {
      showOverlay: isProductOverlay ? brickCode : null,
      originValue: isProductOverlay ? originValue : null,
    };
  };

  const renderProductFieldset = () => {
    return (
      // <Fieldset title='Product information' >
      <Row gutter={16} id='product-field'>
        <Col span={8}>
          <Form.Item
            label='Product Name'
            name='productName'
            rules={[
              {
                required: true,
                message: intl.formatMessage(messages.productNameRequired),
              },
            ]}
          >
            <OverlayInputWrapper {...getOverlayProps('productName')}>
              <Input />
            </OverlayInputWrapper>
          </Form.Item>

          <Form.Item
            label='Short Description'
            name='shortDescription'
            rules={[
              {
                required: true,
                message: 'Short Description is required',
              },
            ]}
          >
            <Input.TextArea
              maxLength={30}
              showCount
              style={{ marginBottom: 4 }}
            />
          </Form.Item>
          <Form.Item label='Long Description' name='longDescription'>
            <Input.TextArea
              maxLength={90}
              rows={4}
              showCount
              style={{ marginBottom: 4 }}
            />
          </Form.Item>
          <Form.Item label='Marketing Description' name='marketingDescription'>
            <Input.TextArea maxLength={1000} rows={8} showCount />
          </Form.Item>

          <Row>
            <Col span={8} />
            <Col span={16}>
              <Form.Item name='brandId'>
                <Form.Item>
                  <div className='add-product__thumb-wrapper'>
                    <div>
                      <CloseOutlined
                        className='add-product__thumb-wrapper-close-btn'
                        onClick={handleResetBrandId}
                      />
                    </div>
                    <ThumbnailItem
                      hideLabel={true}
                      imageThumbnail={brandThumb}
                      hideCheckbox
                      style={{ marginLeft: 0 }}
                    />
                  </div>
                  <Tooltip title='Choose an Brand ID'>
                    <Button onClick={() => setEditBrandVisible(true)}>
                      Link to Brand
                    </Button>
                  </Tooltip>
                </Form.Item>
              </Form.Item>
              {brandError ? (
                <Text type='danger'>{brandErrorMessage}</Text>
              ) : null}
              {editBrandVisible && (
                <ProductEditBrandModal
                  visible={editBrandVisible}
                  setVisibleEditBrand={setEditBrandVisible}
                  productDetail={productData}
                  isAddProduct={true}
                  isEdit={isEdit}
                  setBrandId={setBrandId}
                  setBrandThumb={setBrandThumb}
                  memberIdFromBrand={memberIdBrand}
                />
              )}
            </Col>
          </Row>
        </Col>
        <Col span={8}>
          <Form.Item name='customerSmu' label='Customer / SMU'>
            <OverlayInputWrapper {...getOverlayProps('customerSmu')}>
              <Radio.Group>
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item
            label='UPC'
            name='upc'
            rules={[
              {
                required: true,
                message: 'UPC is required',
              },
            ]}
          >
            <OverlayInputWrapper {...getOverlayProps('upc')}>
              <Input maxLength={MAX_NUMBER_INPUT_UPC} />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item label='Universal Product ID' name='universalProductId'>
            <OverlayInputWrapper {...getOverlayProps('universalProductId')}>
              <Input
                min='0'
                onKeyPress={handleNumberInput}
                maxLength={MAX_NUMBER_INPUT_UPC}
              />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item
            label='SKU'
            name='sku'
            rules={[
              {
                required: true,
                message: 'SKU is required',
              },
            ]}
          >
            <OverlayInputWrapper {...getOverlayProps('sku')}>
              <Input />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item
            label='Model'
            name='model'
            rules={[
              {
                required: true,
                message: 'Model is required',
              },
            ]}
          >
            <OverlayInputWrapper {...getOverlayProps('model')}>
              <Input />
            </OverlayInputWrapper>
          </Form.Item>

          <Form.Item
            label='Width In Inches'
            name='width'
            rules={[
              {
                required: true,
                message: 'Width is required',
              },
            ]}
          >
            <OverlayInputWrapper {...getOverlayProps('width')}>
              <InputNumber
                style={styles.numericInput}
                min={0}
                max={MAX_NUMBER_INPUT_UPC}
              />
            </OverlayInputWrapper>
          </Form.Item>

          <Form.Item
            label='Height In Inches'
            name='height'
            rules={[
              {
                required: true,
                message: 'Height is required',
              },
            ]}
          >
            <OverlayInputWrapper {...getOverlayProps('height')}>
              <InputNumber
                style={styles.numericInput}
                min={0}
                max={MAX_NUMBER_INPUT_UPC}
              />
            </OverlayInputWrapper>
          </Form.Item>

          <Form.Item
            label='Length In Inches'
            name='length'
            rules={[
              {
                required: true,
                message: 'Length is required',
              },
            ]}
          >
            <OverlayInputWrapper {...getOverlayProps('Length')}>
              <InputNumber
                style={styles.numericInput}
                min={0}
                max={MAX_NUMBER_INPUT_UPC}
              />
            </OverlayInputWrapper>
          </Form.Item>

          <Form.Item
            label='Weight In Pounds'
            name='weight'
            rules={[
              {
                required: true,
                message: 'Weight is required',
              },
            ]}
          >
            <OverlayInputWrapper {...getOverlayProps('weight')}>
              <InputNumber
                style={styles.numericInput}
                min={0}
                max={MAX_NUMBER_INPUT_UPC}
              />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item label='Stock Unit of Measure' name='stockUOM'>
            <OverlayInputWrapper {...getOverlayProps('stockUOM')}>
              <Input />
            </OverlayInputWrapper>
          </Form.Item>
          <FormItemCategory />
          <FormItemSubCategory />
          <FormItemClass />
        </Col>
        <Col span={8}>
          <Form.Item name='visibility' label='Visibility'>
            <OverlayInputWrapper {...getOverlayProps('visibility')}>
              {isPrivateVisibilityProduct() ? (
                <WrapperSelect
                  disabled={productData?.gdsnShield}
                  getPopupContainer={() =>
                    document.getElementById('product-field')
                  }
                >
                  <Option value='Only If Shared'>Only If Shared</Option>
                  <Option value='Private'>Private</Option>
                  {/* <Option value='None'>None</Option> */}
                </WrapperSelect>
              ) : (
                <WrapperSelect
                  disabled={productData?.gdsnShield}
                  getPopupContainer={() =>
                    document.getElementById('product-field')
                  }
                >
                  <Option value='Only If Shared'>Only If Shared</Option>
                  <Option value='Public'>Public</Option>
                  <Option value='Private'>Private</Option>
                  {/* <Option value='None'>None</Option> */}
                </WrapperSelect>
              )}
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item
            name='status'
            label='Status'
            //  {...statusValidateProps}
          >
            <OverlayInputWrapper {...getOverlayProps('status')}>
              <WrapperSelect
                getPopupContainer={() =>
                  document.getElementById('product-field')
                }
                disabled={productData?.status?.toLowerCase() === 'exception'}
                // suffixIcon={
                //   isEdit && !isProductionEnv ? (
                //     <ResetFormStatus form={form} productData={productData} />
                //   ) : null
                // }
                // allowClear={isEdit && isProductionEnv ? false : true}
              >
                {[
                  sortBy([...STATUS_PRODUCT], ['displayName']).map(
                    (status, index) => {
                      return (
                        <Option
                          value={status?.value}
                          key={index}
                          // disabled={
                          //   !isProductionEnv &&
                          //   disableStatusSelection({
                          //     valueOption: status.value,
                          //     ...productData,
                          //   })
                          // }
                        >
                          {status?.displayName}
                        </Option>
                      );
                    },
                  ),
                ]}
              </WrapperSelect>
            </OverlayInputWrapper>
          </Form.Item>

          <Form.Item
            label='Manufacturer'
            name='manufacturer'
            rules={[
              {
                required: true,
                message: 'Manufacturer is required',
              },
            ]}
          >
            <OverlayInputWrapper {...getOverlayProps('manufacturer')}>
              <Input />
            </OverlayInputWrapper>
          </Form.Item>

          <Form.Item
            label='Brand'
            name='brandsReportingToMasterAccount'
            rules={[
              {
                required: true,
                message: 'Brand is required',
              },
            ]}
          >
            <OverlayInputWrapper
              {...getOverlayProps('brandsReportingToMasterAccount')}
            >
              <Input />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item
            label='Additional Attributes For End Consumer'
            name='additionalAttributesForEndConsumer'
          >
            <OverlayInputWrapper
              {...getOverlayProps('additionalAttributesForEndConsumer')}
            >
              <Input />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item name='brick' label='Brick Code'>
            <OverlayInputWrapper
              style={{ paddingRight: '10px' }}
              {...getBrickCodeOverlayProps()}
            >
              <BrickCodeInput
                updateBrickCodeData={updateBrickCodeData}
                initialValue={brick}
              />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item label='Brick Code Name'>
            <OverlayInputWrapper {...getOverlayProps('brickCodeName')}>
              <Input value={brick?.brickCodeName} disabled />
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item name='activeUpc' label='Active UPC'>
            <OverlayInputWrapper {...getOverlayProps('activeUpc')}>
              <Radio.Group>
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item name='talo' label='Talo'>
            <OverlayInputWrapper {...getOverlayProps('talo')}>
              <Radio.Group>
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </OverlayInputWrapper>
          </Form.Item>
          <Form.Item name='madeInTheUsa' label='Made in the USA'>
            <Radio.Group>
              <Radio value={true}>Yes</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item label='Features' name='features'>
            <Input.TextArea rows={10} />
          </Form.Item>
        </Col>
      </Row>
      // </Fieldset>
    );
  };

  return (
    <Form
      form={form}
      {...formItemLayout}
      name='Add-Product'
      preserve={false}
      className='add-product__modal'
    >
      <FullWidthSpace size={16}>
        <Row gutter={16}>
          <Col span={24}>{renderProductFieldset()}</Col>
        </Row>
      </FullWidthSpace>
    </Form>
  );
};

const styles = {
  numericInput: {
    width: '100%',
  },
};

const MAX_NUMBER_INPUT_UPC = 9999999999999999999;

const formItemLayout = {
  labelCol: {
    xl: {
      span: 24,
    },
    xxl: {
      span: 8,
    },
  },
  wrapperCol: {
    xl: {
      span: 24,
    },
    xxl: {
      span: 15,
    },
  },
};

export default CreateProductForm;
